import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AssigneeSelector,
  Loader,
  showNotification,
} from "fe-shared/src/components";
import { CloseIcon } from "fe-shared/src/components/Icons";
import UserContext from "contexts/userContext";
import useAssignTransactions from "hooks/useAssignTransactions";

import { ChangeAssignee } from "../../types";

import styles from "./AssigneeBulkSelect.module.scss";

export enum ENTITY {
  Transaction = "transactions",
  InventoryAdjustment = "inventoryAdjustment",
}

interface Props {
  isAssignable: boolean;
  getIds: () => Promise<number[]>;
  onSelfAssignSuccess: () => void;
  changeAssignee?: (
    params: ChangeAssignee
  ) => Promise<{ successCount: number }>;
  entity?: ENTITY;
}

const AssigneeBulkSelect = ({
  isAssignable,
  getIds,
  onSelfAssignSuccess,
  changeAssignee,
  entity = ENTITY.Transaction,
}: Props) => {
  const { t } = useTranslation();
  const { userId: currentUserId } = useContext(UserContext)!;
  const [transactionChangeAssignee] = useAssignTransactions();
  const [isLoading, setIsLoading] = useState(false);

  const changeAssigneeAction = changeAssignee ?? transactionChangeAssignee;

  const onChange = async (newAssigneeId: string | number | null) => {
    try {
      setIsLoading(true);
      const ids = await getIds();
      const result = await changeAssigneeAction({
        ids,
        assigneeId: newAssigneeId,
      });

      if (!result.successCount) {
        showNotification(
          t(`${entity}.grid.bulkAssignError`),
          undefined,
          CloseIcon
        );
        throw new Error("Nothing has been assigned");
      } else {
        showNotification(
          t(`${entity}.grid.bulkAssignSuccess`, {
            successCount: Math.min(ids.length, result.successCount),
            total: ids.length,
          })
        );
      }

      setIsLoading(false);
      if (currentUserId === newAssigneeId) {
        onSelfAssignSuccess();
      }
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.bulkAssign}>
      {isLoading ? (
        <>
          <Loader fontSize={20} className={styles.loader} />
          {t(`${entity}.grid.assigning`)}
        </>
      ) : (
        <AssigneeSelector
          entityId="bulk"
          isAssigned={false}
          isUnAssignable={false}
          loadOnlyActive={true}
          isAssignable={isAssignable}
          currentUserId={currentUserId}
          onChange={onChange}
        />
      )}
    </div>
  );
};

export default AssigneeBulkSelect;
